import { Source } from '../../components/molecules/MonetarySource/MonetarySource';
import { Dayjs } from 'dayjs';
import { AdviceGoalPayloadType } from '../../features/client-goals';

export interface RelationshipType {
    firstName: string;
    lastName: string;
    age: string;
    relationship: string;
    id: string;
    retirementAge?: any;
    planningAge?: any;
}

export interface PersonalInfoCardFFProps {
    setOpenDrawer: React.Dispatch<React.SetStateAction<boolean>>;
    setIsRelationship: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface ClientRowData {
    id: string;
    name: string;
    type: string;
    dob: string;
    retirementAge: string;
    planningAge: string;
    lastName: string;
    customerId?: string;
}

export interface Account {
    accountId: string;
    accountHolder: string;
    accountName?: string;
}

export interface AdviceClient {
    relationship?: { id: string }[];
    accounts?: {
        investmentsSources?: Account[];
        incomeSources?: IncomeSourcesType[];
    };
    loading?: boolean;
    error?: string;
    [key: string]: unknown;
}

// Add type definition for column alignment
export type ColumnAlign = 'left' | 'center' | 'right' | 'inherit' | 'justify';

export interface GoalFormField {
    goalId: string;
    data: RetirementGoalsFormType | WealthGoalsFormType | IncomeGoalsFormType;
}

export interface AdviceGoals {
    formFields: GoalFormField[];
    global?: {
        enablePDFGenerate?: boolean;
        [key: string]: unknown;
    };
}

export type relationshipInput = {
    firstName: string;
    lastName: string;
    age: string;
    relationship: string;
};

export type InvestmentSourcesType = {
    accountId: string;
    accountName: string;
    accountType: string;
    accountHolder: string;
    location: string;
    amount: number;
    recurringContributions: number;
    fundingYear: Dayjs | null;
    frequency: string;
    recurringContributionsStartDate: Dayjs | null;
    recurringContributionsStartDateKey?: string;
    recurringContributionsEndDate: Dayjs | null;
    recurringContributionsEndDateKey?: string;
    escalationContributions: number | null;
    percentage: string;
    oneTimeFutureAmount?: number;
    update?: boolean;
    // AEX-3270: duration/term of the selected account type, stored alongside `accountType`.
    term?: string;
    // AEX-3270: nested contribution funding rows persisted alongside the account.
    // The client-only `localId` is stripped before persisting.
    contributions?: Array<Omit<AccountContribution, 'localId'>>;
    isNew?: boolean;
};
export type IncomeSourcesType = {
    accountId: string;
    sourceName: string;
    sourceType: string;
    amount: number;
    frequency: string;
    beginningYear: string;
    beginningYearKey?: string;
    endYear: string;
    endYearKey?: string;
    escalateIncome: number;
    percentage: string;
    sourceOwner: string;
    update?: boolean;
    isNew?: boolean;
};

export type FundingSource = {
    investmentsSources: Array<InvestmentSourcesType>;
    incomeSources: Array<IncomeSourcesType>;
};

// AEX-3198/AEX-3270: inline Accounts table with nested Contribution Funding rows.
// Field names mirror the persisted `accounts.incomeSources` / `accounts.investmentsSources`
// entities so data syncs without lossy remapping.
// - `localId`: client-only stable key for React/table identity; NEVER sent to the backend.
// - `accountId`: assigned by the backend; absent for rows not yet persisted.
export type AccountContribution = {
    localId: string;
    accountId?: string;
    sourceType: string;
    // AEX-3271: duration/term of the selected source type, stored alongside `sourceType`.
    term?: string;
    sourceOwner: string;
    amount: number;
    frequency: string;
    beginningYear: string;
    endYear: string;
};

export type ClientAccountEntry = {
    localId: string;
    accountId?: string;
    accountType: string;
    // AEX-3271: duration/term of the selected account type, stored alongside `accountType`.
    term?: string;
    accountHolder: string;
    accountName: string;
    amount: number | null;
    contributions: AccountContribution[];
};

export interface AdviceClientType {
    _id: string;
    clientDob: string;
    clientFirstName: string;
    clientLastName: string;
    tenant: string;
    relationship: Array<RelationshipType>;
    riskProfile: string;
    accounts: FundingSource;
    stages: Array<string>;
    loading: boolean;
    error?: null | undefined | any;
    step: number;
    editMode?: boolean;
    hasUnsavedChanges?: boolean;
    enableConfirmConsoleNavigation?: boolean;
    //AEX-1931
    disableConfirmConsoleNavigation?: boolean;
    retirementAge?: any;
    planningAge?: any;
    participant_id?: string;
    isParticipantIdAvailable?: boolean;
    // AEX-3363: optimistic-concurrency reference id echoed back on updateclientprofile so the
    // backend can reject a save made against a stale client copy (plan edited in another tab).
    client_ref_id?: string;
}

export type InvestmentSourcesTypeMap = {
    fundingType: 'incomeSources' | 'investmentAccounts';
    recurringContribution: number;
    escalationPercentage: number;
    amount: number;
    escalationYears: number;
    frequency: string;
    startDate: string;
    endDate: string;
};

export enum GoalPriorityEnum {
    // eslint-disable-next-line no-unused-vars
    NEED = 'Need',
    // eslint-disable-next-line no-unused-vars
    WANT = 'Want',
    // eslint-disable-next-line no-unused-vars
    WISH = 'Wish',
    // eslint-disable-next-line no-unused-vars
    DREAM = 'Dream'
}

export enum Flow2GoalPriorityEnum {
    // eslint-disable-next-line no-unused-vars
    NEED = 'Need',
    // eslint-disable-next-line no-unused-vars
    WANT = 'Want',
    // eslint-disable-next-line no-unused-vars
    WISH = 'Wish',
    // eslint-disable-next-line no-unused-vars
    DREAM = 'Dream',
    // eslint-disable-next-line no-unused-vars
    DESIRE = 'Desire'
}

export enum WealthGoalType {
    // eslint-disable-next-line no-unused-vars
    BUY_A_HOUSE = 'Buy a House',
    // eslint-disable-next-line no-unused-vars
    BUY_A_CAR = 'Buy a Car',
    // eslint-disable-next-line no-unused-vars
    PAY_FOR_COLLEGE = 'Pay for College',
    // eslint-disable-next-line no-unused-vars
    CUSTOM = 'Custom'
}

// eslint-disable-next-line no-unused-vars
export interface WealthGoalsFormType {
    goalName: string;
    goalAmount: number;
    goalType: string;
    wealthGoalType?: GoalTitleEnum;
    goalPriority?: GoalPriorityEnum;
    goalDate?: Dayjs | null;
    goalId: string;
    sources?: { incomeSource: Array<Source>; investmentSource: Array<Source> };
    targetDateKey?: string;
    targetDate?: Dayjs | null;
}

export interface RetirementGoalsFormType {
    title?: GoalTitleEnum;
    goalName: string;
    goalAmount: number | null;
    goalType: string;
    wealthGoalType?: GoalTitleEnum;
    goalPriority: GoalPriorityEnum | undefined;
    goalId: string;
    sources?: { incomeSource: Array<Source>; investmentSource: Array<Source> };
    frequency?: string;
    planStartRetirement: Dayjs | null;
    planStartRetirementKey?: string;
    targetDate?: Dayjs | null;
    targetDateKey?: string;
    yearsInRetirement?: number;
    targetedRetirementIncome: number | undefined;
}

// eslint-disable-next-line no-unused-vars
export interface IncomeGoalsFormType {
    goalName: string;
    goalAmount: number;
    goalType: string;
    wealthGoalType?: GoalTitleEnum;
    goalPriority?: GoalPriorityEnum;
    frequency?: string;
    incomeStartDate?: Dayjs | null;
    incomeEndDate?: Dayjs | null;
    targetDateKey?: string;
    targetDate?: Dayjs | null;
    goalId: string;
    sources?: { incomeSource: Array<Source>; investmentSource: Array<Source> };
}
export type GoalTypeEnum = 'retirement' | 'bequest' | 'buyahouse' | 'buyacar' | 'payforcollege' | 'income' | 'custom';
export type GoalTitleEnum = 'Retirement' | 'Bequest' | 'Buy a House' | 'Buy a Car' | 'Pay for College' | 'Custom';

export const GoalTypeTitlemapping = {
    'Buy a House': 'buyahouse',
    'Buy a Car': 'buyacar',
    'Pay for College': 'payforcollege',
    Custom: 'custom',
    Retirement: 'retirement',
    Bequest: 'bequest'
};

export const GoalTypes = {
    Retirement: 'retirement',
    Wealth: 'wealth',
    Income: 'income'
};

export const TargetDateKeys = {
    client: 'option_0',
    coClient: 'option_1'
};

export interface GoalTypeProps {
    category?: string;
    size?: 'large' | 'medium' | 'small' | number;
    type: GoalTypeEnum | string;
    title?: GoalTitleEnum;
    drawerTitle?: string;
    heading?: string;
    iconName?: string;
    onClickHandler?: () => void;
    isFocused?: boolean | GoalTypeEnum | string;
    disabled?: boolean;
    order?: number;
    backgroundColor?: string;
    darkBackground?: string;
    color?: string;
    icon?: string;
    height?: number;
    width?: number;
}

export interface Flow2GoalTypeProps {
    order: number;
    type: string;
    title: string;
    heading: string;
    disabled: boolean;
    backgroundColor: string;
    darkBackground?: string;
    color: string;
    icon: string;
    drawerTitle?: string;
}

export interface ClientStagesType {
    order: number;
    key: string;
    optional: boolean;
}

export interface ReRunOptionType {
    optionLabel: string;
    key: string;
    // typeof optionValue is number for curreny and string for years
    optionValue: number | string;
}

export interface PlanInfoAndReRunSectionProps {
    initialInvestmentAmount: number;
    originalInvestmentAmount: number | null;
    originalAnnualContributionsAmount: number | null;
    yearsToGoal: string;
    originalYearsToGoals: string | null;
    isRetirement?: boolean;
    yearsInRetirement?: string;
    originalYearsToRetirement: string | null;
    planReRunOptions: Array<ReRunOptionType>;
    isRecomendation?: boolean;
    thresholdValue: number;
    anualContributionDict?: any;
    // eslint-disable-next-line no-unused-vars
    handleReRunButtonClicked: (selectedOption?: ReRunOptionType) => void;
}
export type AssetType = 'Equity' | 'Fixed Income' | 'Cash';

export interface DoughnutChartDataType {
    name: string;
    y: number;
    color: string;
    subAssetClass: string;
    ticker: string;
    tickerLink: string;
}
export interface GroupedAssetAllocation {
    type: string;
    totalAllocatedvalue: string;
    color: string;
    groupedSubAssets: SubAssetGroup[];
}

export interface SubAssetGroup {
    name: string;
    data: SubAssetData[];
    totalAllocatedvalue: string;
    color: string;
}

export interface SubAssetData {
    allocatedValue: number;
    subAssetClass: string;
    ticker: string;
    tickerLink: string;
    tickerName: string;
}
// disable IWS AEX-1611
// export type IwsGoalResponseType = {
//     fundedStatus: string;
//     goal: string;
//     goalId: string;
//     minInitialNeeded: number;
//     origCurrWealth: number;
//     shortfall: number;
//     wealthSplit: number;
// };

export interface GoalRecommendationType {
    goalId: string;
    oneTimeTopUp: number;
    recomendedTenure: number;
    topUpDecumulation: number;
    topUpAccumulation: number;
    orginalAmount?: number;
    originalData?: {
        formFields: any;
        request: any;
        response: any;
        responseGoalCalculator: any;
    };
}
export interface CallRunpipeArgs {
    realloc_schedules: string[];
    orgEmail: string | undefined;
    adviceClient: AdviceClientType;
    adviceGoals: AdviceGoalPayloadType;
    dispatch: any;
    goalData?: any;
    clientData?: any;
}

export type CallRunpipePropsType = (
    /* eslint-disable no-unused-vars */
    realloc_schedules: CallRunpipeArgs['realloc_schedules'],
    orgEmail: CallRunpipeArgs['orgEmail'],
    adviceClient: CallRunpipeArgs['adviceClient'],
    adviceGoals: CallRunpipeArgs['adviceGoals'],
    dispatch: CallRunpipeArgs['dispatch'],
    goalData?: CallRunpipeArgs['goalData'],
    clientData?: CallRunpipeArgs['clientData']
    /* eslint-enable no-unused-vars */
) => void;
